import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Editor } from '@tinymce/tinymce-react';
import { useTinyMceApiKey } from '../hooks/useTinyMceApiKey';

// ─── Exported overlay type (compatible with VideoEditor's TextOverlay) ────────

export interface AssTextOverlay {
  id: string;
  content: string;
  trackStart: number;
  duration: number;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  textAlign: 'left' | 'center' | 'right';
  fontSize: number;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  shadow: boolean;
  font: string;
  bgColor?: string;
  bgOpacity?: number;
  animIn?: 'none' | 'fade' | 'slideUp' | 'zoomIn';
  animOut?: 'none' | 'fade' | 'slideDown' | 'zoomOut';
}

// ─── ASS Types ────────────────────────────────────────────────────────────────

interface AssSubtitle {
  id: string;
  start: number; // seconds
  end: number;
  html: string;  // TinyMCE HTML
  alignment?: number; // local override an1..an9
}

interface AssStyle {
  fontName: string;
  fontSize: number;
  primaryColor: string;
  outlineColor: string;
  bold: boolean;
  italic: boolean;
  alignment: number; // numpad layout: 1-9
  outline: number;
  shadow: number;
  marginV: number;
}

const DEFAULT_ASS_STYLE: AssStyle = {
  fontName: 'Arial', fontSize: 60, primaryColor: '#ffffff', outlineColor: '#000000',
  bold: false, italic: false, alignment: 2, outline: 2, shadow: 2, marginV: 40,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

let _assEid = 0;
function uid(): string { return `ass${++_assEid}_${Math.random().toString(36).slice(2, 6)}`; }

function fmtAssTime(s: number): string {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const cs = Math.floor((s % 1) * 100);
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
}

function parseAssTimecode(v: string): number | null {
  // Accept H:MM:SS.c  H:MM:SS.cc  H:MM:SS.ccc
  const match = v.trim().match(/^(\d+):(\d{2}):(\d{2})[.,](\d{1,3})$/);
  if (!match) return null;
  return +match[1] * 3600 + +match[2] * 60 + +match[3] + +match[4] / (match[4].length === 3 ? 1000 : 100);
}

function cssToAssColor(hex: string): string {
  if (!hex || hex.length < 7) return '&H00FFFFFF';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const h2 = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return `&H00${h2(b)}${h2(g)}${h2(r)}`;
}

function htmlToAssText(html: string): string {
  const withInline = html
    .replace(/<span[^>]*style="[^"]*color:\s*(#[0-9a-fA-F]{6})[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
      (_m: string, color: string, content: string) => `{\\c${cssToAssColor(color)}&}${content}{\\r}`)
    .replace(/<span[^>]*style="[^"]*font-size:\s*(\d+(?:\.\d+)?)pt[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
      (_m: string, size: string, content: string) => `{\\fs${Math.round(+size)}}${content}{\\r}`)
    .replace(/<span[^>]*style="[^"]*font-family:\s*([^;,"]+)[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
      (_m: string, font: string, content: string) => `{\\fn${font.trim()}}${content}{\\r}`);

  return withInline
    .replace(/<br\s*\/?>/gi, '\\N')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\\N')
    .replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, '{\\b1}$1{\\b0}')
    .replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, '{\\b1}$1{\\b0}')
    .replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, '{\\i1}$1{\\i0}')
    .replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, '{\\i1}$1{\\i0}')
    .replace(/<u[^>]*>([\s\S]*?)<\/u>/gi, '{\\u1}$1{\\u0}')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .trim();
}

function htmlToPlainText(html: string): string {
  // Pre-process block-level tags to inject newlines before DOM stripping
  const preprocessed = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n');
  // Use a real DOM element to decode ALL HTML entities (named, numeric, accented chars)
  const div = document.createElement('div');
  div.innerHTML = preprocessed;
  return (div.textContent ?? div.innerText ?? '')
    .replace(/\u00a0/g, ' ')  // &nbsp; → normal space
    .trim();
}

const CSS_TO_FONT_KEY: Record<string, string> = {
  'arial': 'roboto', 'helvetica': 'roboto', 'open sans': 'open-sans',
  'impact': 'bebas', 'times new roman': 'free-serif', 'times': 'free-serif',
  'georgia': 'playfair', 'courier new': 'dejavu-mono', 'courier': 'dejavu-mono',
  'verdana': 'open-sans', 'tahoma': 'open-sans', 'comic sans ms': 'pacifico',
  'roboto': 'roboto', 'montserrat': 'montserrat', 'oswald': 'oswald',
  'bebas neue': 'bebas', 'bebas': 'bebas', 'anton': 'anton', 'bangers': 'bangers',
  'pacifico': 'pacifico', 'lobster': 'lobster', 'dancing script': 'dancing',
};

function rgbToHex(r: string, g: string, b: string): string {
  return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`;
}

function htmlToOverlayStyle(html: string): {
  bold: boolean; italic: boolean; underline: boolean;
  color?: string; fontSize?: number; fontKey?: string; bgColor?: string; bgOpacity?: number;
} {
  const bold = /<(strong|b)[\s>]/i.test(html) || /font-weight:\s*(bold|[789]\d\d)/i.test(html);
  const italic = /<(em|i)[\s>]/i.test(html) || /font-style:\s*italic/i.test(html);
  const underline = /<u[\s>]/i.test(html) || /text-decoration:[^;]*underline/i.test(html);

  const tcHex = html.match(/(?<!background-)color:\s*(#[0-9a-fA-F]{6})/i)?.[1];
  // Also handle rgb() and rgba() — TinyMCE may output either format
  const tcRgb = !tcHex ? html.match(/(?<!background-)color:\s*rgba?\((\d+),\s*(\d+),\s*(\d+)/i) : null;
  const color = tcHex ?? (tcRgb ? rgbToHex(tcRgb[1], tcRgb[2], tcRgb[3]) : undefined);

  // TinyMCE may output pt or px depending on version — support both
  const sizeMatch = html.match(/font-size:\s*([\d.]+)(pt|px)/i);
  let fontSize: number | undefined;
  if (sizeMatch) {
    const val = parseFloat(sizeMatch[1]);
    // Convert px → pt-equivalent (1pt ≈ 1.333px at 96dpi) so the scale stays
    // consistent with assStyle.fontSize which is in ASS/pt units
    fontSize = Math.round(sizeMatch[2].toLowerCase() === 'px' ? val * 0.75 : val);
  }

  // Allow quotes in font-family value so names like "Impact" or "Times New Roman" are captured
  const ffMatch = html.match(/font-family:\s*([^;,<]+)/i);
  let fontKey: string | undefined;
  if (ffMatch) {
    const first = ffMatch[1].trim().replace(/['"]/g, '').split(',')[0].trim().toLowerCase();
    fontKey = CSS_TO_FONT_KEY[first];
  }

  const bgHex = html.match(/background-color:\s*(#[0-9a-fA-F]{6})/i)?.[1];
  const bgRgb = !bgHex ? html.match(/background-color:\s*rgba?\((\d+),\s*(\d+),\s*(\d+)/i) : null;
  const bgColor = bgHex ?? (bgRgb ? rgbToHex(bgRgb[1], bgRgb[2], bgRgb[3]) : undefined);

  return { bold, italic, underline, color, fontSize, fontKey, bgColor, bgOpacity: bgColor ? 85 : undefined };
}

function buildAssFile(subs: AssSubtitle[], style: AssStyle): string {
  const pc = cssToAssColor(style.primaryColor);
  const oc = cssToAssColor(style.outlineColor);
  const bold = style.bold ? '-1' : '0';
  const ital = style.italic ? '-1' : '0';
  const lines = [
    '[Script Info]', 'ScriptType: v4.00+', 'PlayResX: 1920', 'PlayResY: 1080', 'WrapStyle: 0', '',
    '[V4+ Styles]',
    'Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding',
    `Style: Default,${style.fontName},${style.fontSize},${pc},&H000000FF,${oc},&H80000000,${bold},${ital},0,0,100,100,0,0,1,${style.outline},${style.shadow},${style.alignment},10,10,${style.marginV},1`,
    '', '[Events]',
    'Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text',
    ...[...subs].sort((a, b) => a.start - b.start).map(s => {
      const text = htmlToAssText(s.html);
      const hasLocalAlign = Number.isInteger(s.alignment) && (s.alignment as number) >= 1 && (s.alignment as number) <= 9;
      const alignPrefix = hasLocalAlign && s.alignment !== style.alignment ? `{\\an${s.alignment}}` : '';
      return `Dialogue: 0,${fmtAssTime(s.start)},${fmtAssTime(s.end)},Default,,0,0,0,,${alignPrefix}${text}`;
    }),
  ];
  return lines.join('\n') + '\n';
}

function srtToAssSubs(srt: string): AssSubtitle[] {
  const toSec = (h: string, m: string, s: string, ms: string) =>
    +h * 3600 + +m * 60 + +s + +ms / 1000;
  return srt.trim().split(/\n\n+/).flatMap(block => {
    const ls = block.trim().split('\n');
    if (ls.length < 3) return [];
    const m = ls[1].match(/(\d+):(\d+):(\d+),(\d+)\s*-->\s*(\d+):(\d+):(\d+),(\d+)/);
    if (!m) return [];
    return [{ id: uid(), start: toSec(m[1], m[2], m[3], m[4]), end: toSec(m[5], m[6], m[7], m[8]), html: `<p>${ls.slice(2).join('<br />')}</p>` }];
  });
}

type OverlayPosition = {
  x: number; y: number;
  anchorX: number; anchorY: number;
  textAlign: 'left' | 'center' | 'right';
};

const ASS_PLAY_RES_X = 1920;
const ASS_PLAY_RES_Y = 1080;
const ASS_MARGIN_LR = 10;

function assAlignmentToOverlay(alignment: number, marginV: number): OverlayPosition {
  const isLeft = [1, 4, 7].includes(alignment);
  const isRight = [3, 6, 9].includes(alignment);
  const isTop = [7, 8, 9].includes(alignment);
  const isBottom = [1, 2, 3].includes(alignment);
  const marginVPercent = Math.max(0, Math.min(49.5, (Math.max(0, marginV) / ASS_PLAY_RES_Y) * 100));
  const marginHPercent = Math.max(0.5, (ASS_MARGIN_LR / ASS_PLAY_RES_X) * 100);
  return {
    x: isLeft ? marginHPercent : isRight ? 100 - marginHPercent : 50,
    y: isTop ? marginVPercent : isBottom ? 100 - marginVPercent : 50,
    anchorX: isLeft ? 0 : isRight ? 1 : 0.5,
    anchorY: isTop ? 0 : isBottom ? 1 : 0.5,
    textAlign: isLeft ? 'left' : isRight ? 'right' : 'center',
  };
}

// ─── Timeline → ASS helpers ───────────────────────────────────────────────────

/** Minimal subset of TextOverlay needed for timeline import. */
export interface TimelineText {
  id: string;
  content: string;
  trackStart: number;
  duration: number;
  anchorX?: number;
  anchorY?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  fontSize?: number;
}

/** Convert anchor coordinates to ASS numpad alignment (1–9). */
function overlayToAssAlignment(anchorX?: number, anchorY?: number): number {
  const ax = anchorX ?? 0.5;
  const ay = anchorY ?? 1; // subtitle default = bottom
  const col = ax < 0.25 ? 0 : ax > 0.75 ? 2 : 1; // 0=left,1=center,2=right
  const row = ay > 0.75 ? 0 : ay < 0.25 ? 2 : 1; // 0=bottom,1=middle,2=top
  return [[1, 2, 3], [4, 5, 6], [7, 8, 9]][row][col];
}

/** Convert a timeline TextOverlay into an AssSubtitle with reconstructed HTML. */
function timelineTextToAssSubtitle(t: TimelineText): AssSubtitle {
  let inner = t.content.replace(/\n/g, '<br/>');
  if (t.underline) inner = `<u>${inner}</u>`;
  if (t.italic)    inner = `<em>${inner}</em>`;
  if (t.bold)      inner = `<strong>${inner}</strong>`;
  const styles: string[] = [];
  if (t.color)    styles.push(`color: ${t.color}`);
  if (t.fontSize) styles.push(`font-size: ${t.fontSize}pt`);
  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';
  return {
    id: uid(),
    start: t.trackStart,
    end: t.trackStart + t.duration,
    html: `<p${styleAttr}>${inner}</p>`,
    alignment: overlayToAssAlignment(t.anchorX, t.anchorY),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AssSubtitleEditorProps {
  show: boolean;
  onClose: () => void;
  subtitleSrt?: string | null;
  timelineTexts?: TimelineText[];
  videoRef: React.RefObject<HTMLVideoElement | null>;
  fileName: string;
  /** newAssTextIds = IDs of the new overlays; replacedTimelineIds = original timeline IDs to remove */
  onApply: (newTexts: AssTextOverlay[], newAssTextIds: string[], replacedTimelineIds: string[]) => void;
}

const AssSubtitleEditor: React.FC<AssSubtitleEditorProps> = ({
  show, onClose, subtitleSrt, timelineTexts, videoRef, fileName, onApply,
}) => {
  const { apiKey: tinyApiKey } = useTinyMceApiKey();
  const [assSubtitles, setAssSubtitles] = useState<AssSubtitle[]>([]);
  const [assSelectedId, setAssSelectedId] = useState<string | null>(null);
  const [assStyle, setAssStyle] = useState<AssStyle>({ ...DEFAULT_ASS_STYLE });
  /** IDs of the timeline text overlays that were imported (to replace them on apply). */
  const [importedTimelineIds, setImportedTimelineIds] = useState<string[]>([]);

  /** Load subtitles from timeline text blocks (sorted by start time). */
  const importFromTimeline = () => {
    if (!timelineTexts?.length) return;
    const sorted = [...timelineTexts].sort((a, b) => a.trackStart - b.trackStart);
    const imported = sorted.map(timelineTextToAssSubtitle);
    setAssSubtitles(imported);
    setAssSelectedId(imported[0]?.id ?? null);
    setImportedTimelineIds(sorted.map(t => t.id));
    // Sync assStyle with the first imported text so the panel reflects
    // the actual attributes of the existing overlays (avoids overwriting
    // them with DEFAULT_ASS_STYLE values when Apply is clicked)
    const first = sorted[0];
    setAssStyle(s => ({
      ...s,
      ...(first.color ? { primaryColor: first.color } : {}),
      ...(first.fontSize && first.fontSize > 0 ? { fontSize: first.fontSize } : {}),
      bold: first.bold ?? s.bold,
      italic: first.italic ?? s.italic,
    }));
  };

  // Auto-import on first open: prefer timeline texts, fall back to raw SRT
  useEffect(() => {
    if (!show || assSubtitles.length > 0) return;
    if (timelineTexts && timelineTexts.length > 0) {
      importFromTimeline();
    } else if (subtitleSrt) {
      const imported = srtToAssSubs(subtitleSrt);
      setAssSubtitles(imported);
      setAssSelectedId(imported[0]?.id ?? null);
      setImportedTimelineIds([]);
    }
  }, [show]); // eslint-disable-line

  if (!show) return null;

  const selected = assSubtitles.find(s => s.id === assSelectedId) ?? null;

  const updateSub = (id: string, patch: Partial<AssSubtitle>) =>
    setAssSubtitles(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));

  const addSub = () => {
    const last = [...assSubtitles].sort((a, b) => a.start - b.start).at(-1);
    const start = last ? last.end : (videoRef.current?.currentTime ?? 0);
    const newSub: AssSubtitle = { id: uid(), start, end: start + 3, html: '<p>Nouveau sous-titre</p>' };
    setAssSubtitles(prev => [...prev, newSub]);
    setAssSelectedId(newSub.id);
  };

  const removeSub = (id: string) => {
    setAssSubtitles(prev => {
      const next = prev.filter(s => s.id !== id);
      if (assSelectedId === id) setAssSelectedId(next[0]?.id ?? null);
      return next;
    });
  };

  const applyToTimeline = () => {
    const newTexts: AssTextOverlay[] = assSubtitles
      .filter(s => s.start < s.end && s.html.trim())
      .map(s => {
        const lineAlignment = Number.isInteger(s.alignment) ? (s.alignment as number) : assStyle.alignment;
        const assPos = assAlignmentToOverlay(lineAlignment, assStyle.marginV);
        const inlineStyle = htmlToOverlayStyle(s.html);
        return {
          id: uid(), content: htmlToPlainText(s.html),
          trackStart: s.start, duration: s.end - s.start,
          x: assPos.x, y: assPos.y,
          anchorX: assPos.anchorX, anchorY: assPos.anchorY, textAlign: assPos.textAlign,
          fontSize: inlineStyle.fontSize ?? assStyle.fontSize,
          color: inlineStyle.color ?? assStyle.primaryColor,
          bold: inlineStyle.bold || assStyle.bold,
          italic: inlineStyle.italic || assStyle.italic,
          underline: inlineStyle.underline,
          shadow: assStyle.outline > 0,
          font: inlineStyle.fontKey ?? CSS_TO_FONT_KEY[assStyle.fontName.toLowerCase()] ?? 'open-sans',
          ...(inlineStyle.bgColor ? { bgColor: inlineStyle.bgColor, bgOpacity: inlineStyle.bgOpacity ?? 85 } : {}),
        };
      });
    onApply(newTexts, newTexts.map(t => t.id), importedTimelineIds);
    onClose();
  };

  const downloadAss = () => {
    const blob = new Blob([buildAssFile(assSubtitles, assStyle)], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${fileName.replace(/\.[^.]+$/, '')}.ass`;
    a.click();
  };

  const importFromSrt = () => {
    if (!subtitleSrt) return;
    const subs = srtToAssSubs(subtitleSrt);
    setAssSubtitles(subs);
    setAssSelectedId(subs[0]?.id ?? null);
  };

  const colOf = (a: number) => [1, 4, 7].includes(a) ? 0 : [3, 6, 9].includes(a) ? 2 : 1;
  const rowOf = (a: number) => [7, 8, 9].includes(a) ? 0 : [4, 5, 6].includes(a) ? 1 : 2;
  const zoneLabels: Record<number, string> = { 7: 'Haut G.', 8: 'Haut', 9: 'Haut D.', 4: 'Gauche', 5: 'Centre', 6: 'Droite', 1: 'Bas G.', 2: 'Bas', 3: 'Bas D.' };

  const previewText = selected ? (htmlToPlainText(selected.html) || 'Aperçu') : 'Aperçu du sous-titre';
  const previewOutline = assStyle.outline > 0
    ? `0 0 ${assStyle.outline * 2}px ${assStyle.outlineColor}, 1px 1px ${assStyle.outline}px ${assStyle.outlineColor}, -1px -1px ${assStyle.outline}px ${assStyle.outlineColor}`
    : assStyle.shadow > 0 ? `${assStyle.shadow}px ${assStyle.shadow}px ${assStyle.shadow * 2}px #000` : 'none';
  const effectiveAlignment = (selected && Number.isInteger(selected.alignment)) ? (selected.alignment as number) : assStyle.alignment;
  const previewPos = assAlignmentToOverlay(effectiveAlignment, assStyle.marginV);

  const inp: React.CSSProperties = { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 };
  const lbl: React.CSSProperties = { fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 3 };
  const sectionHead = (num: string, title: string, hint: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
      <span style={{ background: '#4f46e5', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{num}</span>
      <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 13 }}>{title}</span>
      <span style={{ color: '#64748b', fontSize: 11 }}>{hint}</span>
    </div>
  );

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Force TinyMCE floating UI above this modal */}
      <style>{`.tox-tinymce-aux { z-index: 1000002 !important; }`}</style>
      <div style={{ background: '#1e293b', borderRadius: 10, width: 'min(1140px,97vw)', maxHeight: '93vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.8)', overflow: 'hidden' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: '#0f172a', borderBottom: '1px solid #334155', flexShrink: 0 }}>
          <div>
            <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 14 }}>💬 Éditeur de sous-titres ASS/SSA</span>
            <span style={{ color: '#475569', fontSize: 12, marginLeft: 12 }}>{assSubtitles.length} ligne{assSubtitles.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(timelineTexts && timelineTexts.length > 0) && (
              <button onClick={importFromTimeline}
                title="Recharger les blocs texte actuels de la timeline (écrase les lignes en cours)"
                style={{ background: '#0891b2', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
                ⬇ Importer depuis la timeline
              </button>
            )}
            {subtitleSrt && (
              <button onClick={importFromSrt} title="Importer les sous-titres générés par le transcripteur IA"
                style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
                ⬇ Importer depuis SRT
              </button>
            )}
            <button onClick={applyToTimeline} disabled={assSubtitles.length === 0}
              title="Convertit toutes les lignes en blocs texte sur la timeline vidéo"
              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 12, cursor: 'pointer', opacity: assSubtitles.length ? 1 : 0.4 }}>
              ✅ Appliquer à la timeline
            </button>
            <button onClick={downloadAss} disabled={assSubtitles.length === 0}
              title="Télécharger le fichier .ass — utilisable dans VLC, ffmpeg, etc."
              style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 12, cursor: 'pointer', opacity: assSubtitles.length ? 1 : 0.4 }}>
              💾 Télécharger .ass
            </button>
            <button onClick={onClose}
              style={{ background: '#334155', color: '#94a3b8', border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

          {/* ── Colonne gauche : liste des lignes ── */}
          <div style={{ width: 230, flexShrink: 0, background: '#0f172a', borderRight: '1px solid #1e293b', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 10px 8px', borderBottom: '1px solid #1e293b' }}>
              <button onClick={addSub}
                style={{ width: '100%', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 5, padding: '7px', fontSize: 12, cursor: 'pointer', fontWeight: 700, letterSpacing: '0.02em' }}>
                + Ajouter une ligne
              </button>
              <div style={{ fontSize: 10, color: '#475569', marginTop: 6, lineHeight: 1.4 }}>
                Chaque ligne = un sous-titre avec son timecode
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {assSubtitles.length === 0 && (
                <div style={{ color: '#475569', fontSize: 12, padding: '16px 12px', textAlign: 'center', lineHeight: 1.6 }}>
                  Aucune ligne.<br />
                  <span style={{ color: '#64748b' }}>Cliquez "+ Ajouter une ligne"<br />ou importez un SRT.</span>
                </div>
              )}
              {[...assSubtitles].sort((a, b) => a.start - b.start).map((sub, i) => {
                const isActive = assSelectedId === sub.id;
                return (
                  <div key={sub.id} onClick={() => setAssSelectedId(sub.id)}
                    style={{ padding: '9px 10px', cursor: 'pointer', borderBottom: '1px solid #1e293b', background: isActive ? '#1e3a5f' : 'transparent', borderLeft: isActive ? '3px solid #4f46e5' : '3px solid transparent', transition: 'background 0.1s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: isActive ? '#93c5fd' : '#475569', marginBottom: 3, fontFamily: 'monospace' }}>
                          #{i + 1} &nbsp;{fmtAssTime(sub.start)} → {fmtAssTime(sub.end)}
                        </div>
                        <div style={{ fontSize: 12, color: isActive ? '#e2e8f0' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sub.html.replace(/<[^>]+>/g, '').trim() || <em style={{ opacity: 0.4 }}>vide</em>}
                        </div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); removeSub(sub.id); }}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', opacity: 0.6, flexShrink: 0 }}
                        title="Supprimer cette ligne">×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Colonne droite : édition ── */}
          <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
            {!selected ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#475569' }}>
                <div style={{ fontSize: 40 }}>💬</div>
                <div style={{ fontSize: 14, textAlign: 'center', lineHeight: 1.7 }}>
                  Sélectionnez une ligne dans la liste<br />
                  <span style={{ fontSize: 12 }}>ou cliquez <strong style={{ color: '#3b82f6' }}>+ Ajouter une ligne</strong> pour commencer.</span>
                </div>
              </div>
            ) : (
              <div style={{ padding: 16 }}>

                {/* ── Section ① : Timecodes ── */}
                <div style={{ marginBottom: 20 }}>
                  {sectionHead('1', 'Quand apparaît ce sous-titre ?', '— définissez le début et la fin')}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, padding: '12px 14px' }}>
                    <div>
                      <label style={lbl}>Début (H:MM:SS.cs)</label>
                      <input key={`start-${selected.id}-${selected.start}`} style={{ ...inp, width: 130, fontFamily: 'monospace' }}
                        defaultValue={fmtAssTime(selected.start)}
                        onBlur={e => { const v = parseAssTimecode(e.target.value); if (v !== null) updateSub(selected.id, { start: v }); }} />
                    </div>
                    <div style={{ color: '#475569', paddingBottom: 6, fontSize: 18 }}>→</div>
                    <div>
                      <label style={lbl}>Fin (H:MM:SS.cs)</label>
                      <input key={`end-${selected.id}-${selected.end}`} style={{ ...inp, width: 130, fontFamily: 'monospace' }}
                        defaultValue={fmtAssTime(selected.end)}
                        onBlur={e => { const v = parseAssTimecode(e.target.value); if (v !== null) updateSub(selected.id, { end: v }); }} />
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
                      <button onClick={() => updateSub(selected.id, { start: videoRef.current?.currentTime ?? selected.start })}
                        title="Utiliser la position actuelle du curseur vidéo comme timecode de début"
                        style={{ background: '#0c4a6e', color: '#7dd3fc', border: '1px solid #0369a1', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>
                        ⏱ Début = curseur vidéo
                      </button>
                      <button onClick={() => updateSub(selected.id, { end: videoRef.current?.currentTime ?? selected.end })}
                        title="Utiliser la position actuelle du curseur vidéo comme timecode de fin"
                        style={{ background: '#0c4a6e', color: '#7dd3fc', border: '1px solid #0369a1', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>
                        ⏱ Fin = curseur vidéo
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Section ② : TinyMCE ── */}
                <div style={{ marginBottom: 20 }}>
                  {sectionHead('2', 'Que dit ce sous-titre ?', '— tapez et formatez le texte de cette ligne')}
                  <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                      ✏️ L'éditeur ci-dessous contrôle <strong style={{ color: '#94a3b8' }}>uniquement ce sous-titre</strong>.
                      Les mises en forme <strong style={{ color: '#7dd3fc' }}>Gras</strong> et <em style={{ color: '#7dd3fc' }}>Italique</em> seront converties
                      en balises ASS <code style={{ background: '#1e293b', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>{'{\\b1}'}</code> / <code style={{ background: '#1e293b', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>{'{\\i1}'}</code> lors de l'export.
                    </div>
                  </div>
                  <Editor
                    key={selected.id}
                    apiKey={tinyApiKey}
                    value={selected.html}
                    onEditorChange={html => updateSub(selected.id, { html })}
                    init={{
                      z_index: 1000001,
                      height: 220,
                      menubar: false,
                      toolbar_mode: 'wrap',
                      skin: 'oxide-dark',
                      content_css: 'dark',
                      plugins: ['lists'],
                      toolbar: 'fontfamily fontsize | bold italic underline forecolor backcolor | alignleft aligncenter alignright | bullist | removeformat',
                      font_size_formats: '16pt 20pt 24pt 28pt 32pt 36pt 48pt 60pt 72pt 96pt',
                      font_family_formats: [
                        'Arial=arial,helvetica,sans-serif',
                        'Impact=impact,charcoal',
                        'Times New Roman=times new roman,times',
                        'Courier New=courier new,courier,monospace',
                        'Verdana=verdana,geneva',
                        'Tahoma=tahoma,sans-serif',
                        'Georgia=georgia,serif',
                        'Comic Sans MS=comic sans ms,cursive',
                      ].join(';'),
                      color_map: [
                        'FFFFFF', 'Blanc',
                        'FFFF00', 'Jaune',
                        'FF0000', 'Rouge',
                        '00FF00', 'Vert',
                        '00FFFF', 'Cyan',
                        'FF00FF', 'Magenta',
                        'FF8800', 'Orange',
                        '000000', 'Noir',
                        '94a3b8', 'Gris clair',
                        '1e3a5f', 'Bleu foncé',
                      ],
                      color_default_foreground: '#FFFFFF',
                      content_style: 'body { font-family: Arial, sans-serif; font-size: 24pt; color: #ffffff; background: #0f172a; padding: 10px; line-height: 1.5; margin: 0; }',
                    }}
                  />
                </div>

                {/* ── Section ③ : Aperçu + Style ── */}
                <div>
                  {sectionHead('3', 'Apparence & position', selected ? '— position de ce sous-titre (cliquez une zone) + style global ASS' : '— réglages globaux appliqués à toutes les lignes du fichier ASS')}
                  <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {selected ? (
                        <>🎯 <strong style={{ color: '#94a3b8' }}>Cliquez une zone</strong> pour positionner <strong style={{ color: '#7dd3fc' }}>ce sous-titre</strong> spécifiquement.
                          {Number.isInteger(selected.alignment) && (
                            <> Position forcée : <strong style={{ color: '#818cf8' }}>{zoneLabels[selected.alignment as number]}</strong>
                              <button onClick={() => updateSub(selected.id, { alignment: undefined })}
                                style={{ marginLeft: 6, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 11, padding: 0 }}>✕ Revenir au global</button>
                            </>
                          )}
                        </>
                      ) : (
                        <>🎨 Ces réglages définissent le <strong style={{ color: '#94a3b8' }}>style global</strong> du fichier ASS. Sélectionnez un sous-titre pour lui imposer une position spécifique.</>
                      )}
                    </div>
                  </div>

                  {/* Aperçu interactif */}
                  <div style={{ position: 'relative', background: 'linear-gradient(135deg,#020617 0%,#0f172a 100%)', borderRadius: 6, overflow: 'hidden', border: '1px solid #334155', marginBottom: 14 }}>
                    <div style={{ paddingBottom: '42%', position: 'relative' }}>
                      {[7, 8, 9, 4, 5, 6, 1, 2, 3].map(n => {
                        const zc = colOf(n), zr = rowOf(n);
                        const isActive = effectiveAlignment === n;
                        const isPerLine = selected && selected.alignment === n;
                        const isGlobalOnly = !isPerLine && assStyle.alignment === n;
                        return (
                          <div key={n}
                            onClick={() => {
                              if (selected) {
                                updateSub(selected.id, { alignment: selected.alignment === n ? undefined : n });
                              } else {
                                setAssStyle(s => ({ ...s, alignment: n }));
                              }
                            }}
                            title={selected ? `Forcer la position "${zoneLabels[n]}" pour ce sous-titre` : `Positionner : ${zoneLabels[n]}`}
                            style={{
                              position: 'absolute',
                              left: `${zc * 33.33}%`, top: `${zr * 33.33}%`,
                              width: '33.33%', height: '33.33%',
                              border: isPerLine ? '2px solid #818cf8' : isActive ? '2px solid #4f46e5' : '1px solid rgba(255,255,255,0.06)',
                              background: isPerLine ? 'rgba(129,140,248,0.2)' : isActive ? 'rgba(79,70,229,0.18)' : 'transparent',
                              cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'background 0.15s',
                              boxSizing: 'border-box',
                            }}
                            onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.04)'; }}
                            onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = isPerLine ? 'rgba(129,140,248,0.2)' : isActive ? 'rgba(79,70,229,0.18)' : 'transparent'; }}>
                            {isActive && (
                              <span style={{ fontSize: 9, color: isPerLine ? '#c7d2fe' : '#818cf8', background: isPerLine ? 'rgba(99,102,241,0.7)' : 'rgba(79,70,229,0.5)', padding: '2px 5px', borderRadius: 3, pointerEvents: 'none' }}>
                                {zoneLabels[n]}{isGlobalOnly && selected ? ' ●' : ''}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {/* Texte aperçu */}
                      <div style={{
                        position: 'absolute',
                        left: `${previewPos.x}%`, top: `${previewPos.y}%`,
                        transform: `translate(${-previewPos.anchorX * 100}%, ${-previewPos.anchorY * 100}%)`,
                        color: assStyle.primaryColor,
                        fontFamily: `${assStyle.fontName}, Arial, sans-serif`,
                        fontSize: `${Math.max(11, Math.min(assStyle.fontSize * 0.32, 28))}px`,
                        fontWeight: assStyle.bold ? 'bold' : 'normal',
                        fontStyle: assStyle.italic ? 'italic' : 'normal',
                        textShadow: previewOutline,
                        textAlign: previewPos.textAlign,
                        maxWidth: '80%',
                        lineHeight: 1.3,
                        pointerEvents: 'none',
                        padding: '0 8px',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {previewText}
                      </div>
                      {/* Légende */}
                      <div style={{ position: 'absolute', bottom: 4, right: 8, fontSize: 9, color: '#334155', pointerEvents: 'none' }}>
                        {selected ? 'Cliquez pour imposer la position à ce sous-titre' : 'Cliquez pour changer la position globale'}
                      </div>
                    </div>
                  </div>

                  {/* Contrôles style global ASS */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, padding: '12px 14px' }}>
                    <div>
                      <label style={lbl}>Couleur du texte</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="color" value={assStyle.primaryColor}
                          onChange={e => setAssStyle(s => ({ ...s, primaryColor: e.target.value }))}
                          style={{ width: 36, height: 30, borderRadius: 4, border: '1px solid #334155', cursor: 'pointer', padding: 1, background: 'none' }} />
                        <span style={{ fontSize: 10, color: '#64748b' }}>{assStyle.primaryColor}</span>
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>Police (global)</label>
                      <select value={assStyle.fontName}
                        onChange={e => setAssStyle(s => ({ ...s, fontName: e.target.value }))}
                        style={{ ...inp, width: 140 }}>
                        {['Arial', 'Impact', 'Times New Roman', 'Verdana', 'Tahoma', 'Georgia', 'Comic Sans MS', 'Courier New'].map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Taille police (ASS px)</label>
                      <input type="number" min={10} max={200} value={assStyle.fontSize}
                        onChange={e => setAssStyle(s => ({ ...s, fontSize: Math.max(10, Math.min(200, +e.target.value || 60)) }))}
                        style={{ ...inp, width: 70 }} />
                    </div>
                    <div>
                      <label style={lbl}>Gras / Italique</label>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={() => setAssStyle(s => ({ ...s, bold: !s.bold }))}
                          style={{ background: assStyle.bold ? '#4f46e5' : '#334155', color: '#fff', border: 'none', borderRadius: 4, width: 30, height: 30, cursor: 'pointer', fontWeight: 'bold', fontSize: 14 }}>B</button>
                        <button onClick={() => setAssStyle(s => ({ ...s, italic: !s.italic }))}
                          style={{ background: assStyle.italic ? '#4f46e5' : '#334155', color: '#fff', border: 'none', borderRadius: 4, width: 30, height: 30, cursor: 'pointer', fontStyle: 'italic', fontSize: 14 }}>I</button>
                      </div>
                    </div>
                    <div style={{ width: '100%', height: 0, margin: '4px 0', borderTop: '1px solid #1e293b' }} />
                    <div>
                      <label style={lbl}>Contour — couleur</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="color" value={assStyle.outlineColor}
                          onChange={e => setAssStyle(s => ({ ...s, outlineColor: e.target.value }))}
                          style={{ width: 36, height: 30, borderRadius: 4, border: '1px solid #334155', cursor: 'pointer', padding: 1, background: 'none' }} />
                        <span style={{ fontSize: 10, color: '#64748b' }}>{assStyle.outlineColor}</span>
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>Épaisseur contour</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="range" min={0} max={10} value={assStyle.outline}
                          onChange={e => setAssStyle(s => ({ ...s, outline: +e.target.value }))}
                          style={{ width: 80 }} />
                        <span style={{ fontSize: 12, color: '#94a3b8', minWidth: 24 }}>{assStyle.outline}px</span>
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>Ombre portée</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="range" min={0} max={10} value={assStyle.shadow}
                          onChange={e => setAssStyle(s => ({ ...s, shadow: +e.target.value }))}
                          style={{ width: 80 }} />
                        <span style={{ fontSize: 12, color: '#94a3b8', minWidth: 24 }}>{assStyle.shadow}px</span>
                      </div>
                    </div>
                    <div>
                      <label style={lbl}>Marge verticale (ASS)</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="range" min={0} max={200} value={assStyle.marginV}
                          onChange={e => setAssStyle(s => ({ ...s, marginV: +e.target.value }))}
                          style={{ width: 90 }} />
                        <span style={{ fontSize: 12, color: '#94a3b8', minWidth: 30 }}>{assStyle.marginV}px</span>
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: 10, color: '#334155', alignSelf: 'center', maxWidth: 180, lineHeight: 1.5 }}>
                      ↑ Style global appliqué à tout le fichier ASS.<br />
                      <span style={{ color: '#475569' }}>Pour formater ligne par ligne → barre TinyMCE (Section 2)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AssSubtitleEditor;
